import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { IpcClient, type IpcResult } from "./ipc-client";
import { getBridgeTransport, makeHybridTransport } from "./transport";
import { HttpTransport, isWebClient } from "./http-transport";
import { buildMockTransport } from "./mock-data";

const IpcContext = createContext<IpcClient | null>(null);

/** engineIds cuyo `system.engine.overview` sirve un motor real (el resto es mock). */
const REAL_ENGINE_IDS = new Set<string>(["product", "marketplace", "inventory", "sales", "pricing", "purchase", "supplier", "logistics", "customer", "analytics", "finance", "ai"]);

/** Canales servidos por el backend real (rebanadas verticales conectadas). */
function isRealChannel(channel: string, payload?: unknown): boolean {
  if (channel.startsWith("product_development.")) return true;
  if (channel.startsWith("settings.credentials.")) return true;
  if (channel.startsWith("settings.onboarding.")) return true;
  if (channel.startsWith("settings.business.")) return true;
  if (channel.startsWith("settings.sync.")) return true;
  if (channel.startsWith("integrations.")) return true;
  if (channel.startsWith("market.trends.")) return true;
  if (channel.startsWith("analytics.targets.")) return true;
  if (channel.startsWith("accounts.")) return true;
  if (channel.startsWith("system.backup.")) return true;
  if (channel.startsWith("system.notifications.")) return true;
  if (channel === "system.export.save") return true;
  if (channel === "system.export.saveImage") return true;
  if (channel.startsWith("product.images.")) return true;
  if (channel.startsWith("system.update.")) return true;
  if (channel.startsWith("product.")) return true;
  if (channel.startsWith("catalog.")) return true;
  if (channel.startsWith("customer.")) return true;
  if (channel.startsWith("supplier.")) return true;
  if (channel.startsWith("sales.")) return true;
  if (channel.startsWith("inventory.")) return true;
  if (channel.startsWith("purchase.")) return true;
  if (channel.startsWith("pricing.")) return true;
  if (channel.startsWith("logistics.")) return true;
  if (channel.startsWith("marketplace.")) return true;
  if (channel.startsWith("ai.")) return true;
  if (channel.startsWith("brain.")) return true;
  if (channel === "system.engine.overview") {
    const engineId = (payload as { engineId?: string } | undefined)?.engineId;
    return engineId !== undefined && REAL_ENGINE_IDS.has(engineId);
  }
  return false;
}

/**
 * Usa el puente del preload si existe (Electron): enruta a él los canales del backend
 * real y al transporte simulado el resto. Sin puente, todo es simulado.
 */
export function defaultClient(): IpcClient {
  const bridge = getBridgeTransport();
  if (bridge) return new IpcClient(makeHybridTransport(bridge, buildMockTransport(), isRealChannel));
  // PWA servida por el servidor: TODO va al backend real por HTTP.
  if (isWebClient()) return new IpcClient(new HttpTransport());
  // Desarrollo (vite dev) o tests sin cliente inyectado: datos de ejemplo.
  return new IpcClient(buildMockTransport());
}

export function IpcProvider({ client, children }: { client?: IpcClient; children: ReactNode }): JSX.Element {
  const value = useMemo(() => client ?? defaultClient(), [client]);
  return <IpcContext.Provider value={value}>{children}</IpcContext.Provider>;
}

export function useIpc(): IpcClient {
  const client = useContext(IpcContext);
  if (!client) throw new Error("useIpc debe usarse dentro de <IpcProvider>");
  return client;
}

export type AsyncState<T> =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly error: { readonly code: string; readonly message: string } }
  | { readonly status: "ready"; readonly data: T };

/** Hook genérico de carga: ejecuta una consulta IPC y expone loading/error/ready. */
export function useIpcQuery<T>(query: (client: IpcClient) => Promise<IpcResult<T>>, deps: readonly unknown[]): {
  state: AsyncState<T>;
  reload: () => void;
} {
  const client = useIpc();
  const [state, setState] = useState<AsyncState<T>>({ status: "loading" });
  const [nonce, setNonce] = useState(0);
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    let active = true;
    setState({ status: "loading" });
    queryRef.current(client).then((result) => {
      if (!active) return;
      setState(result.ok ? { status: "ready", data: result.value } : { status: "error", error: result.error });
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, nonce, ...deps]);

  return { state, reload: () => setNonce((n) => n + 1) };
}
